import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { concat, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  flatMap,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { StatementInfo } from './models/statement-info.model';
import { StatementMetrics } from './models/statement-metrics.model';
import {
  ActionsEnum,
  fetchStatementInfo,
  FetchStatementInfo,
  fetchStatementInfoError,
  FetchStatementInfoError,
  fetchStatementInfoSuccess,
  FetchStatementInfoSuccess,
  FetchStatementMetrics,
  fetchStatementMetricsError,
  FetchStatementMetricsError,
  fetchStatementMetricsSuccess,
  FetchStatementMetricsSuccess,
  RefreshStatementInfo,
  SendStatement,
  SendStatementError,
  sendStatementError,
  SendStatementSuccess,
  sendStatementSuccess,
  UpdateStatementInfo,
  updateStatementInfo,
  UpdateStatementMetrics,
  updateStatementMetrics,
} from './statement-data.actions';
import { StatementDataBuilder } from './statement-data.builder';
import { StatementDataService } from './statement-data.service';
import { IStatementInfoParams } from './statement-data.data';
import { ClientDataDispatcher } from '../client-data/client-data.dispatcher';
import { Client } from '../client-data/client.model';
import { StatementDataStore } from './statement-data.store';

@Injectable()
export class StatementDataEffects {
  private _refreshStatementMetricTimer: { [clientId: string]: boolean } = {};

  constructor(
    private _actions: Actions,
    private _settingsDataStore: SettingsDataStore,
    private _statementDataStore: StatementDataStore,
    private _statementDataBuilder: StatementDataBuilder,
    private _statementDataService: StatementDataService,
    private _clientDataDispatcher: ClientDataDispatcher
  ) {}

  @Effect({ dispatch: false })
  clearStatementMetricTimer: Observable<any> = this._actions.pipe(
    ofType(ActionsEnum.UPDATE_STATEMENT_METRICS),
    tap(({ payload }: UpdateStatementMetrics) => {
      const { clientId, statementMetrics } = payload;

      if (!statementMetrics) {
        this._refreshStatementMetricTimer[clientId] = false;
      }
    })
  );

  @Effect()
  getStatementInfo: Observable<
    UpdateStatementInfo | FetchStatementInfoSuccess | FetchStatementInfoError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_STATEMENT_INFO),
    switchMap(({ payload }: FetchStatementInfo) => {
      const { dispatchId, clientId, startDate, endDate, unpaidOnly } = payload;

      return this._clientDataDispatcher.getEntityById(clientId).pipe(
        withLatestFrom(this._settingsDataStore.settings$),
        flatMap(([client, settings]: [Client, Settings]) => {
          const stateInfoParams: IStatementInfoParams = this._statementDataBuilder.buildStatementsPayload(
            clientId,
            client.billingName,
            startDate,
            endDate,
            unpaidOnly,
            settings.documentCurrencyCode
          );

          return this._statementDataService
            .getStatementInfo(stateInfoParams)
            .pipe(
              map((statementInfo: StatementInfo) =>
                this._statementDataBuilder.buildStatementInfo(
                  statementInfo,
                  settings.documentPresetSettings,
                  settings.companyInfo,
                  client
                )
              )
            );
        }),
        flatMap((statementInfo: StatementInfo) =>
          concat([
            updateStatementInfo(clientId, statementInfo),
            fetchStatementInfoSuccess(dispatchId, clientId),
          ])
        ),
        catchError((error) =>
          of(fetchStatementInfoError(dispatchId, clientId, error))
        )
      );
    })
  );

  @Effect()
  refreshStatementInfo: Observable<FetchStatementInfo> = this._actions.pipe(
    ofType(ActionsEnum.REFRESH_STATEMENT_INFO),
    switchMap(({ payload }: RefreshStatementInfo) => {
      const { dispatchId, clientId } = payload;

      return this._statementDataStore.getStatementInfo(clientId).pipe(
        take(1),
        filter((x) => !!x),
        map((statementInfo: StatementInfo) => {
          const statementContent = statementInfo.statement.content;

          return fetchStatementInfo(
            dispatchId,
            clientId,
            statementContent.startDate,
            statementContent.endDate,
            statementContent.unpaidOnly
          );
        })
      );
    })
  );

  @Effect()
  getStatementMetrics: Observable<
    | UpdateStatementMetrics
    | FetchStatementMetricsSuccess
    | FetchStatementMetricsError
  > = this._actions.pipe(
    ofType(ActionsEnum.FETCH_STATEMENT_METRICS),
    withLatestFrom(this._settingsDataStore.settings$),
    switchMap(([{ payload }, settings]: [FetchStatementMetrics, Settings]) => {
      const { clientId, params, dispatchId } = payload;
      const statementMetricParams = {
        ...params,
        currency_code: settings.documentCurrencyCode,
      };

      return this._statementDataService
        .getStatementMetrics(statementMetricParams)
        .pipe(
          flatMap((statementMetrics: StatementMetrics) =>
            concat([
              updateStatementMetrics(clientId, statementMetrics),
              fetchStatementMetricsSuccess(dispatchId, clientId),
            ])
          ),
          catchError((error) =>
            of(fetchStatementMetricsError(dispatchId, clientId, error))
          )
        );
    })
  );

  @Effect()
  sendStatement: Observable<
    SendStatementSuccess | SendStatementError
  > = this._actions.pipe(
    ofType(ActionsEnum.SEND_STATEMENT),
    switchMap(({ payload }: SendStatement) => {
      const { clientId, statementData, dispatchId } = payload;

      return this._statementDataService
        .sendStatement(clientId, statementData)
        .pipe(
          map(() => sendStatementSuccess(dispatchId, clientId)),
          catchError((error) =>
            of(sendStatementError(dispatchId, clientId, error))
          )
        );
    })
  );
}
